/**
 * GuestHome — streamlined, logged-out copy of screens/Home/index.js.
 *
 * Intended path: src/screens/GuestHome/index.js (sibling of src/screens/Home).
 * Home/index.js itself is untouched — this is a separate component/route,
 * per your call. It reuses Home.module.scss and SkeletonComponents as-is
 * (imported with a '../Home/...' relative path) rather than duplicating
 * them, since the visual language is identical and only the data/sections
 * differ.
 *
 * WHAT'S DROPPED vs Home:
 *  - "Followed Businesses" section entirely (businesses/follow,
 *    businesses/all, customerCoupons/followedCoupons — all gone).
 *  - "Favourites" section entirely (customerCoupons/favourite gone).
 *    isFavourite is now always false; there is nothing to check it against.
 *  - customerCoupons/applied + the user-limit `blockedIds` logic — that's
 *    about a logged-in customer's own redemption history, meaningless for
 *    a guest.
 *  - customerProfile fetch/seeding — no profile exists pre-login. City
 *    bootstrap now only reads localStorage, it no longer falls back to
 *    customerProfile.customer_city.
 *  - The 3:1 "interleave followed coupons with general coupons" logic —
 *    with no followed list, the feed is just the plain paginated
 *    Newest Coupons list.
 *
 * WHAT'S KEPT, unchanged from Home:
 *  - Hero (city picker + category picker + search) — browsing by
 *    location/category needs no auth.
 *  - "Explore deals by category" carousel.
 *  - "Newest Coupons" grid, pagination (IntersectionObserver), and the
 *    SponsoredCard inserted at index 8 — identical to Home.
 *  - Tapping into a coupon's details (goToDetails) is left open to guests;
 *    per your note you're separately making the backend routes for this
 *    accessible without auth, so the frontend here assumes that will work.
 *
 * WHAT'S GATED BEHIND LOGIN (new):
 *  - The favourite heart on a coupon card (toggleFavourite).
 *  - Following a business, if/when that entry point is added to this
 *    screen later.
 *  Both now call `requireAuth(...)` instead of dispatching directly.
 *  requireAuth just opens the inline <LoginPromptModal> below — you chose
 *  "show a modal in place" over a redirect. There's no existing shared
 *  login modal in the 5 files you gave me, so I built a minimal
 *  self-contained one rather than guess an import path. If the codebase
 *  already has one (e.g. `components/LoginModal`), swap
 *  <LoginPromptModal /> for that and delete the local one below — it's
 *  isolated at the bottom of this file for exactly that swap.
 *
 * ASSUMPTIONS THAT NEED YOUR CONFIRMATION — I couldn't verify these from
 * the uploaded files, so they're flagged rather than silently guessed:
 *  1. Auth check: `state.auth.user` (per your answer "Firebase auth user /
 *     JWT in redux"). If your auth slice has a different shape/name
 *     (e.g. `state.auth.currentUser`, `state.firebase.auth`), update the
 *     one `useSelector` call marked ASSUMPTION below — everything else
 *     (`isGuest`) derives from it.
 *  2. RouteNames.LOGIN / RouteNames.SIGNUP — used by the modal's buttons.
 *     Adjust to whatever your actual route constants are named.
 *  3. Route registration for this screen (e.g. RouteNames.GUEST_HOME at
 *     '/welcome') isn't in any file I have — you'll need to add it to
 *     your router config yourself.
 *
 * MULTI-CITY SEARCH HANDOFF — the hero Search button and the "Explore
 * deals by category" cards now forward the confirmed `selectedCities`
 * array to CouponsListAll via `navigate(..., { state: { preSelectedCities } })`,
 * the same way `preSelectedCategories` is already forwarded. This mirrors
 * CouponsListAll's own working multi-city pattern (a local array seeded
 * once from redux's single activeCity, then driven independently) rather
 * than touching the redux `cities` slice, which stays single-value.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import cx from 'classnames';
import CSSModule from 'react-css-modules';
import { ChevronDown, ChevronUp, Grid, X } from 'react-feather';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CityPickerButtonModal, CustomerCouponCard, Spinner, SeoHead } from 'components';
import { SITE_URL } from 'components/SeoHead/SeoHead';
import { COUPON_TYPES } from 'coupon-common';
import { actions as cityActions } from 'coupon-common/src/modules/cities';
import { actions } from 'coupon-common/src/modules/customerCoupons/all';
import { actions as categoryActions } from 'coupon-common/src/modules/customerCoupons/categories';

import foodDrinkIcon from '../../assets/images/categories/CategoryFoodDrink.webp';
import selfCareIcon from '../../assets/images/categories/CategorySelfCare.webp';
import servicesIcon from '../../assets/images/categories/CategoryServices.webp';
import shoppingIcon from '../../assets/images/categories/CategoryShopping.webp';
import thingsToDoIcon from '../../assets/images/categories/CategoryThingsToDo.webp';
import CoffeeShopBG from '../../assets/images/CoffeeShopBG.webp';
import LoadingLogo from '../../components/LoadingLogo/LoadingLogo';
import SponsoredCard from '../../components/SponsoredCard/SponsoredCard';
import { RouteNames } from '../../constants';
// resolveImageUrl: same helper CouponsDetails.js uses to turn a coupon's
// raw image_url entry into a displayable URL — reused here (not
// duplicated) for the structured-data `image` field below.
import { resolveImageUrl } from '../../utils/cleanExternalUrls';
// Reused as-is from Home — see file header for why these aren't duplicated.
import styles from '../Home/Home.module.scss';
import { NewestCouponsSkeleton } from '../Home/SkeletonComponents';
import { Helmet } from 'react-helmet';
import { OK } from 'http-status-codes';
import { getNearestCity } from 'coupon-common/src/modules/locations/api';
import { actions as locationActions } from 'coupon-common/src/modules/locations';


import { useScreenTracking } from '../../utils/useScreenTracking'; // adjust path
import TrackedCouponCard from '../../components/TrackedCouponCard/TrackedCouponCard'; // adjust path

const CACHE_KEYS = {
  DEFAULT_CITY: 'DEFAULT_CITY',
};

/**
 * SESSION-SCOPED FETCH CACHE — plain module-scope variable, NOT
 * sessionStorage/localStorage. It persists for as long as this JS module
 * stays loaded, which in a client-side-routed SPA is the whole browser
 * tab session: React Router unmounts this component on navigation away,
 * but never re-evaluates this module, so the variable survives. A hard
 * refresh re-evaluates the whole bundle from scratch and resets it to
 * null automatically — exactly the "cache for the session, reset on hard
 * refresh or a real input change" behavior wanted.
 *
 * Tracks only the city we last fetched coupons for. The coupons
 * themselves (including anything picked up via infinite scroll) already
 * live in redux (customerCoupons.all) and survive navigation on their
 * own — redux isn't torn down on unmount. The only reason coupons were
 * disappearing on navigate-back was that the fetch effect below used to
 * unconditionally call resetCouponList() + re-fetch page 1 on every
 * mount, because its old "have we already fetched?" flag lived in a
 * useRef, which — unlike this module-scope variable — does NOT survive
 * unmount/remount.
 */
let guestHomeFetchCache = null; // { lastFetchedCityId }

// Guests have no customerProfile.customer_city to fall back to (unlike
// Home), so first-time visitors with nothing in localStorage default to
// this city instead of never fetching coupons at all.
const GUEST_DEFAULT_CITY = { id: 347, name: 'Halifax' };

const categoryIcons = {
  'Food & Drink': foodDrinkIcon,
  'Things To Do': thingsToDoIcon,
  Shopping: shoppingIcon,
  'Self Care': selfCareIcon,
  Services: servicesIcon,
};
const targetCategoryNames = ['Food & Drink', 'Things To Do', 'Shopping', 'Self Care', 'Services'];

// Same amount/percentage/points-back switch CouponsDetails.js uses inline
// for its own `dealAmount` — pulled into a helper here since it's needed
// once per coupon when building the ItemList below, not just once per page.
const getDealAmount = coupon => {
  if (!coupon) return null;
  if (coupon.type === COUPON_TYPES.AMOUNT_OFF) return `$${coupon.amount_off} OFF`;
  if (coupon.type === COUPON_TYPES.PERCENTAGE_OFF) return `${Math.round(coupon.percentage_off)}% OFF`;
  if (coupon.type === COUPON_TYPES.POINTS_BACK) return `${coupon.points_back} Points Back`;
  return null;
};

/** Identical to Home's HeroDropdownPortal — portals the open Category list to document.body. */
const HeroDropdownPortal = ({ triggerRef, portalRef, isOpen, children }) => {
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    const updateCoords = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 6, left: rect.left, minWidth: rect.width });
    };
    updateCoords();

    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [isOpen, triggerRef]);

  if (!isOpen || !coords) return null;

  return createPortal(
    <div
      ref={portalRef}
      className={styles.dropdown}
      style={{ position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.minWidth }}
    >
      {children}
    </div>,
    document.body,
  );
};

const SectionHeaderBase = ({ title, actionLabel, onAction }) => (
  <div styleName="title-row">
    <div styleName="title-with-dot">
      <span styleName="title-dot" />
      <h2 styleName="section-title">{title}</h2>
    </div>
    {onAction && (
      <button type="button" styleName="see-more-btn" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
);
const SectionHeader = CSSModule(SectionHeaderBase, styles, { allowMultiple: true });

const ArrowButton = ({ dir, onClick, ariaLabel }) => (
  <button type="button" styleName={cx('arrow-btn', dir === 'left' ? 'arrow-left' : 'arrow-right')} onClick={onClick} aria-label={ariaLabel}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d={dir === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} />
    </svg>
  </button>
);
const ArrowBtn = CSSModule(ArrowButton, styles, { allowMultiple: true });

/** Same scroll-position-aware carousel hook as Home — used here only for the category carousel. */
const useCarousel = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scroll = useCallback(
    dir => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({ left: dir === 'left' ? -260 : 260, behavior: 'smooth' });
      setTimeout(updateScrollState, 350);
    },
    [updateScrollState],
  );

  return { scrollRef, canScrollLeft, canScrollRight, updateScrollState, scroll };
};

/**
 * Minimal, self-contained login/signup prompt — see file header note #1.
 * Swap this out wholesale if the codebase already has a shared modal.
 */
const LoginPromptModalBase = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4000,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Log in required"
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: '28px 24px',
          width: '100%',
          maxWidth: 360,
          textAlign: 'center',
          position: 'relative',
          fontFamily: "'Poppins', Helvetica, sans-serif",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#a3a3a3',
          }}
        >
          <X size={18} />
        </button>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px', color: '#171717' }}>Log in to continue</h2>
        <p style={{ fontSize: 14, color: '#737373', margin: '0 0 20px' }}>
          Create a free account (or log in) to save favourites, follow your favourite businesses, and redeem coupons.
        </p>
        <button
          type="button"
          onClick={() => navigate(RouteNames.SIGN_IN)}
          style={{
            width: '100%',
            padding: '12px 0',
            borderRadius: 999,
            border: 'none',
            background: 'linear-gradient(90deg, #ef4444, #7f1d1d)',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            marginBottom: 10,
          }}
        >
          Log In
        </button>
        <button
          type="button"
          onClick={() => navigate(RouteNames.SIGN_UP)}
          style={{
            width: '100%',
            padding: '12px 0',
            borderRadius: 999,
            border: '1.5px solid #e5e5e5',
            background: '#fff',
            color: '#171717',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Sign Up
        </button>
      </div>
    </div>
  );
};

const GuestHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useScreenTracking('GuestHome');

  // ASSUMPTION (confirm #1 in file header): adjust if your auth slice
  // is shaped differently. isGuest should be true whenever there's no
  // logged-in Firebase user / JWT.
  const authUser = useSelector(s => (s.auth && s.auth.user) || null);
  const isGuest = !authUser;

  // ======= Redux selects (public data only) =======
  const customerCouponsAllState = useSelector(s => s.customerCoupons.all || {}, shallowEqual);
  const { fetching, items, result } = customerCouponsAllState;
  const { results = [], nextPage } = result || {};

  const activeCityId = useSelector(s => (s.cities && s.cities.activeCityId) || null);
  const activeCity = useSelector(s => (s.cities && s.cities.activeCity) || null);

  const categoryState = useSelector(state => state.customerCoupons.categories || {});
  const dynamicCategories = (categoryState.data && categoryState.data.results) || categoryState.data || [];

  // ======= Local state =======
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);

  const [selectedCities, setSelectedCities] = useState([]);
  const [pendingCities, setPendingCities] = useState([]);
  const hasInitialisedCities = useRef(false);
  const [heroCategory, setHeroCategory] = useState('');
  const [isHeroCategoryOpen, setIsHeroCategoryOpen] = useState(false);
  const heroCategoryDropdownRef = useRef(null);
  const heroCategoryPortalRef = useRef(null);

  const resultsLengthRef = useRef(result?.results?.length ?? 0);

  // Runs any action that requires a logged-in user; opens the login
  // prompt instead when the visitor is a guest.
  const requireAuth = useCallback(
    action => {
      if (isGuest) {
        setIsLoginPromptOpen(true);
        return;
      }
      action();
    },
    [isGuest],
  );

     // ======= City bootstrap ======= (localStorage only — no customerProfile pre-login)
  //
  // Order: localStorage (explicit prior choice) > nearest-city-by-IP
  // (first-time visitor) > hardcoded GUEST_DEFAULT_CITY (nearest-city
  // lookup failed or returned nothing). The nearest-city lookup goes
  // through redux/saga (locations/getNearestCity) since apiCall is a
  // saga effect, not a promise — it can't be .then()'d directly here.
  const nearestCityState = useSelector(
    s => (s.locations && s.locations.nearestCity) || { fetching: false, fetched: false, data: null, error: null },
    shallowEqual,
  );
  const nearestCityDispatched = useRef(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CACHE_KEYS.DEFAULT_CITY);
      if (stored) {
        const parsed = JSON.parse(stored);
        dispatch(cityActions.setActiveCity(parsed.name, parsed.id));
        return;
      }
    } catch (e) {
      console.warn('Could not read default city from localStorage', e);
      dispatch(cityActions.setActiveCity(GUEST_DEFAULT_CITY.name, GUEST_DEFAULT_CITY.id));
      return;
    }

    // No stored city — ask the backend (via saga) to resolve one from the visitor's IP.
    nearestCityDispatched.current = true;
    dispatch(locationActions.getNearestCity());
  }, [dispatch]);

  // Reacts once the nearest-city saga above resolves (success or failure).
  useEffect(() => {
    if (!nearestCityDispatched.current || !nearestCityState.fetched) return;

    if (nearestCityState.data && nearestCityState.data.id) {
      const { id, name } = nearestCityState.data;
      dispatch(cityActions.setActiveCity(name, id));
      try {
        localStorage.setItem(CACHE_KEYS.DEFAULT_CITY, JSON.stringify({ name, id }));
      } catch (e) {
        console.warn('Could not persist nearest city to localStorage', e);
      }
    } else {
      console.warn('Nearest-city lookup failed, using guest default', nearestCityState.error);
      dispatch(cityActions.setActiveCity(GUEST_DEFAULT_CITY.name, GUEST_DEFAULT_CITY.id));
    }

    // Stops this effect from re-firing on unrelated nearestCityState changes later.
    nearestCityDispatched.current = false;
  }, [nearestCityState, dispatch]);

  useEffect(() => {
    if (!activeCityId || !activeCity) return;

    if (!hasInitialisedCities.current) {
      const initial = [{ id: activeCityId, name: activeCity }];
      setSelectedCities(initial);
      setPendingCities(initial);
      hasInitialisedCities.current = true;
    } else {
      setSelectedCities(prev => (prev.some(c => c.id === activeCityId) ? prev : [{ id: activeCityId, name: activeCity }]));
      setPendingCities(prev => (prev.some(c => c.id === activeCityId) ? prev : [{ id: activeCityId, name: activeCity }]));
    }
  }, [activeCityId, activeCity]);

  const handleCityConfirm = useCallback(() => {
    setSelectedCities(pendingCities);
    const chosen = pendingCities[0];
    if (!chosen) return;
    dispatch(cityActions.setActiveCity(chosen.name, chosen.id));
    try {
      localStorage.setItem(CACHE_KEYS.DEFAULT_CITY, JSON.stringify({ name: chosen.name, id: chosen.id }));
    } catch (e) {
      console.warn('Could not persist default city to localStorage', e);
    }
  }, [pendingCities, dispatch]);

  const heroLocationLabel = selectedCities.length > 0 ? selectedCities.map(c => c.name).join(', ') : 'Select Location';

  useEffect(() => {
    const handleClickOutside = e => {
      const insideCategory =
        (heroCategoryDropdownRef.current && heroCategoryDropdownRef.current.contains(e.target)) ||
        (heroCategoryPortalRef.current && heroCategoryPortalRef.current.contains(e.target));
      if (!insideCategory) {
        setIsHeroCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ======= Derivations =======
  // No hasFreePlan filtering here — that came from customerProfile, which
  // guests don't have. If exclusive-paid coupons need to stay hidden from
  // guests too, that filter belongs here — flag if so and I'll add it.
  const couponList = useMemo(() => {
    if (!results || !items) return [];
    const mapped = results.map(id => items[id]);
    const missingIds = results.filter(id => !items[id]);
    // if (missingIds.length > 0) {
    //   console.log('[GuestHome] couponList: ids in results with no matching item', missingIds);
    // }
    // console.log('[GuestHome] couponList: results.length', results.length, '-> couponList.length', mapped.filter(Boolean).length);
    return mapped.filter(Boolean);
  }, [results, items]);

  const displayedCouponList = useMemo(() => {
    const now = new Date();

    const dropped = [];
    const result = couponList.reduce((acc, coupon) => {
      if (coupon.expiry_type === 'date' && coupon.expiry_date) {
        const exp = new Date(coupon.expiry_date);
        if (!isNaN(+exp) && exp < now) {
          dropped.push({ id: coupon.id, expiry_type: coupon.expiry_type, raw_expiry_date: coupon.expiry_date, resolved_expiry_local: exp.toString() });
          return acc;
        }
        acc.push(coupon);
      } else {
        acc.push(coupon);
      }
      return acc;
    }, []);

  

    return result;
  }, [couponList]);

  // ======= Data fetchers =======
  const getAllList = useCallback(
    (params = {}) => {
      const finalParams = { page: params.page ?? 1, ...(activeCityId ? { city: activeCityId } : {}) };
      dispatch(actions.getAllList(finalParams));
    },
    [dispatch, activeCityId],
  );

  useEffect(() => {
    if (!activeCityId) return;
    if (guestHomeFetchCache?.lastFetchedCityId === activeCityId) {
      // Already fetched (and possibly paginated further via scroll) for
      // this exact city earlier in this session — redux still holds that
      // data, so just reuse it instead of wiping it out and re-fetching
      // page 1. This is the only branch that changes on navigate-back;
      // everything below is unchanged from before.
      return;
    }
    // A next-page fetch that was already in flight for the *previous* city
    // has no bearing on the city we're switching to — clear the flag so a
    // stale response landing after this point can't wedge future
    // scroll-triggered loads (see handleLoadMore's `fetching` guard below).
    setLoadingNextPage(false);
    // getAllList always sends an explicit `page` (defaulting to 1), which
    // routes every fetch — including this "fresh page 1 for the new city"
    // one — through SUCCESS_PAGINATED (`attach: true` in duck.js), i.e. it
    // merges onto whatever's already in the customerCoupons/all normalized
    // state rather than replacing it. resetCouponList is the action
    // duck.js already defines for exactly this: wipe back to
    // EXTEND_NORMALIZED_INITIAL_STATE so the incoming attach-merge has
    // nothing stale left to merge onto. (Same fix as Home/index.js — see
    // that file for the full diagnosis.)
    dispatch(actions.resetCouponList());
    getAllList({ page: 1 });
    guestHomeFetchCache = { lastFetchedCityId: activeCityId };
  }, [activeCityId, dispatch, getAllList]);

  useEffect(() => {
    dispatch(categoryActions.getCategoryList());
  }, [dispatch]);

  // ======= Handlers =======
  const goToDetails = useCallback(
    coupon => {
      if (coupon.state === 'expired') {
        // eslint-disable-next-line no-alert
        window.alert('Coupon Expired\n\nSorry, this coupon has expired. It may be available again in the future.');
        return;
      }
      // Left open to guests — see file header re: backend route access.
      navigate(`${RouteNames.COUPONS_DETAILS}/${coupon.id}`);
    },
    [navigate],
  );

  const goToBusiness = useCallback(
    obj => {
      if (obj && obj.id && !obj.business) {
        navigate(`${RouteNames.BUSINESSES_DETAILS}/${obj.id}`);
        return;
      }
      if (obj?.business?.id) {
        const route = obj.is_affiliate_or_flyer ? RouteNames.AFFILIATE_BUSINESS_DETAILS : RouteNames.BUSINESSES_DETAILS;
        navigate(`${route}/${obj.business.id}`);
      }
    },
    [navigate],
  );

  // Guests have no favourites list to check against; the heart always
  // renders as "not favourited" and tapping it prompts login.
  const checkFavourite = useCallback(() => false, []);
  const toggleFavouriteHandler = useCallback(() => {
    requireAuth(() => {
      // A logged-in favourite dispatch has nothing to do here — by the
      // time isGuest is false the visitor should already be on the real
      // Home screen, not this one. Left as a no-op guard.
    });
  }, [requireAuth]);

  const followBusinessHandler = useCallback(() => {
    requireAuth(() => {
      // No-op for the same reason as toggleFavouriteHandler — a
      // logged-in user shouldn't be on this screen.
    });
  }, [requireAuth]);

  // ======= Pagination (IntersectionObserver) =======
  const handleLoadMore = useCallback(() => {
    // `fetching` is included so the scroll-sentinel can't fire a "next
    // page" request while a city switch's page-1 fetch is still in
    // flight — see Home/index.js for the full race explanation.
    if (!nextPage || loadingNextPage || fetching) return;
    setLoadingNextPage(true);
    getAllList({ page: nextPage });
  }, [nextPage, loadingNextPage, fetching, getAllList]);

  useEffect(() => {
    const currentLen = result?.results?.length ?? 0;
    if (loadingNextPage && currentLen > resultsLengthRef.current) {
      setLoadingNextPage(false);
    }
    resultsLengthRef.current = currentLen;
  }, [loadingNextPage, result?.results?.length]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) handleLoadMore();
      },
      { rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  const exploreCategories = useMemo(
    () =>
      targetCategoryNames
        .map(name => {
          const match = dynamicCategories.find(cat => cat.name?.trim().toLowerCase() === name.trim().toLowerCase());
          return match ? { id: match.id, icon: categoryIcons[name], name } : null;
        })
        .filter(Boolean),
    [dynamicCategories],
  );

  const heroCategoryLabel = useMemo(() => {
    const match = exploreCategories.find(c => String(c.id) === heroCategory);
    return match ? match.name : 'Category';
  }, [exploreCategories, heroCategory]);

  const isInitialLoad = fetching && !result?.results?.length;

  const categoryCarousel = useCarousel();
  useEffect(() => {
    categoryCarousel.updateScrollState();
  }, [exploreCategories.length, categoryCarousel]);


  // ─── SEO ────────────────────────────────────────────────────────────
// This is the logged-out landing page — the one page in the app crawlers
// and unauthenticated visitors actually see, unlike Home.js which sits
// behind auth. Title/description reflect the active city (once known);
// structuredData lists the currently-visible coupons.
// ASSUMPTION: RouteNames.GUEST_HOME isn't confirmed registered anywhere
// in the files I have (see file header note #3) — adjust `path` below
// once that route constant actually exists.
// const seoTitle = activeCity ? `Deals in ${activeCity}` : 'Discover Deals Near You';
//PETER UPDATE SEPT 29/26 - Updating title & desc as per Nelson's request (https://ccmycouponbook.atlassian.net/browse/MYC-590)
const seoTitle = 'Canadian Deals & Local Coupons | My Couponbook';
const seoDescription = "Connect with local businesses and start saving today. Find Canada's best deals, exclusive coupons, and online promo codes from your favourite brands.";
// const seoDescription = `Browse the latest coupons and deals${
//   activeCity ? ` in ${activeCity}` : ' across Canada'
// } on My Couponbook — no account needed to browse.`;
// ASSUMPTION: each coupon in displayedCouponList carries an embedded
// `business` object (at least {id, name}) — inferred from goToBusiness's
// `obj.business.id` check and CustomerCouponCard rendering a business
// logo/name straight off `data={item}` with no separate `business` prop
// here (unlike BusinessesDetails.js, which passes one explicitly because
// its coupons don't have it attached). If that's wrong for this endpoint,
// `seller` below will just come out undefined per-item — harmless, but
// flagging so you can confirm the field actually exists in the guest
// listing response.
const structuredData =
  displayedCouponList.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: displayedCouponList.slice(0, 50).map((coupon, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Offer',
            name: coupon.title,
            url: `${SITE_URL}${RouteNames.COUPONS_DETAILS}/${coupon.id}`,
            image: resolveImageUrl(coupon.image_url?.[0]?.image_url) || undefined,
            description: getDealAmount(coupon) || undefined,
            seller: coupon.business?.name ? { '@type': 'LocalBusiness', name: coupon.business.name } : undefined,
          },
        })),
      }
    : undefined;

  return (
    <div styleName="page">
 <SeoHead
       title={seoTitle}
       description={seoDescription}
       image={CoffeeShopBG}
       path={RouteNames.GUEST_HOME}
       type="website"
       structuredData={structuredData}
     />
      {isInitialLoad && (
        <div styleName="loading-overlay">
          <LoadingLogo size={96} label="Loading deals…" />
        </div>
      )}
      <div styleName="hero">
        <img src={CoffeeShopBG} alt="" styleName="hero-bg" />
        <div styleName="hero-overlay" />
        <div styleName="hero-content">
          <h1 styleName="hero-title">Discover deals near you</h1>
          <p styleName="hero-subtitle">Fresh coupons from the local spots you already love.</p>

          <div styleName="hero-search-bar">
            <div styleName="hero-city-picker">
              <CityPickerButtonModal
                label={heroLocationLabel}
                selectedCities={pendingCities}
                onChangeSelected={setPendingCities}
                onConfirm={handleCityConfirm}
                confirmLabel="Search"
              />
            </div>
            <div styleName="hero-category-picker" ref={heroCategoryDropdownRef}>
              <button type="button" styleName="hero-select-trigger" onClick={() => setIsHeroCategoryOpen(open => !open)}>
                <span styleName="hero-select-text">{heroCategoryLabel}</span>
                {isHeroCategoryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              <HeroDropdownPortal triggerRef={heroCategoryDropdownRef} portalRef={heroCategoryPortalRef} isOpen={isHeroCategoryOpen}>
                <button
                  type="button"
                  styleName={cx('dropdown-item', 'dropdown-item-all', { active: heroCategory === '' })}
                  onClick={() => {
                    setHeroCategory('');
                    setIsHeroCategoryOpen(false);
                  }}
                >
                  <Grid size={15} styleName="dropdown-item-feather-icon" />
                  <span>All categories</span>
                </button>
                {exploreCategories.map(item => (
                  <button
                    type="button"
                    key={item.id}
                    styleName={cx('dropdown-item', { active: heroCategory === String(item.id) })}
                    onClick={() => {
                      setHeroCategory(String(item.id));
                      setIsHeroCategoryOpen(false);
                    }}
                  >
                    <img src={item.icon} alt="" styleName="dropdown-item-icon" />
                    <span>{item.name}</span>
                  </button>
                ))}
              </HeroDropdownPortal>
            </div>
            <button
              type="button"
              styleName="hero-search-btn"
              onClick={() => {
                console.log('GuestHome: Search clicked with heroCategory', heroCategory);

                const navState = {};
                if (heroCategory) navState.preSelectedCategories = [Number(heroCategory)];
                if (selectedCities.length > 0) navState.preSelectedCities = selectedCities;

                if (heroCategory) {
                  dispatch(categoryActions.setPreselectedCategories([Number(heroCategory)]));
                }

                navigate(RouteNames.COUPONS_LISTS_ALL, Object.keys(navState).length > 0 ? { state: navState } : undefined);
              }}
            >
              Search
            </button>
          </div>
        </div>
      </div>

      <div styleName="wrap">
        {/* Explore Deals by Category */}
        <div styleName="section">
          <SectionHeader title="Explore deals by category" actionLabel="Search More" onAction={() => navigate(RouteNames.COUPONS_LISTS_ALL)} />

          <div styleName="carousel">
            {categoryCarousel.canScrollLeft && (
              <ArrowBtn dir="left" ariaLabel="Scroll categories left" onClick={() => categoryCarousel.scroll('left')} />
            )}
            <div ref={categoryCarousel.scrollRef} onScroll={categoryCarousel.updateScrollState} styleName="h-scroll">
              {exploreCategories.map(item => (
                <button
                  key={item.id}
                  type="button"
                  styleName="category-card"
                  onClick={() => {
                    dispatch(categoryActions.setPreselectedCategories([item.id]));
                    navigate(RouteNames.COUPONS_LISTS_ALL, {
                      state: {
                        preSelectedCategories: [item.id],
                        ...(selectedCities.length > 0 ? { preSelectedCities: selectedCities } : {}),
                      },
                    });
                  }}
                >
                  <img src={item.icon} alt={item.name} styleName="category-card-img" />
                  <div styleName="category-card-overlay" />
                  <div styleName="category-card-label-wrap">
                    <p styleName="category-card-label">{item.name}</p>
                  </div>
                </button>
              ))}
            </div>
            {categoryCarousel.canScrollRight && (
              <ArrowBtn dir="right" ariaLabel="Scroll categories right" onClick={() => categoryCarousel.scroll('right')} />
            )}
          </div>
        </div>

        <div styleName="separator" />

        {/* Newest Coupons */}
        <div styleName="section">
          <SectionHeader title="Newest Coupons" actionLabel="See All" onAction={() => navigate(RouteNames.COUPONS_LISTS_ALL)} />

          {displayedCouponList.length === 0 && !isInitialLoad && <p styleName="no-items-text">No coupons available in this city.</p>}

          {isInitialLoad ? (
            <NewestCouponsSkeleton />
          ) : (
            <div styleName="coupons-grid">
              {displayedCouponList.map((item, index) => (
                <React.Fragment key={item.id}>
                  <TrackedCouponCard
                      item={item}
                      index={index}
                      feedId="guest_home_newest_coupons"
                      feedSort="newest"
                      first={index === 0}
                      isFavourite={checkFavourite(item)}
                      onPress={() => goToDetails(item)}
                      goToBusinessDetails={() => goToBusiness(item)}
                      toggleFavourite={() => toggleFavouriteHandler(item.id)}
                      isGuest={isGuest}
                      onGuestAction={followBusinessHandler}
                    />
                  {/* <CustomerCouponCard
                    first={index === 0}
                    data={item}
                    onPress={() => goToDetails(item)}
                    isFavourite={checkFavourite(item)}
                    goToBusinessDetails={() => goToBusiness(item)}
                    toggleFavourite={() => toggleFavouriteHandler(item.id)}
                    isGuest={isGuest} 
                    onGuestAction={followBusinessHandler}
                  /> */}
                  {index === 8 && <SponsoredCard key="sponsored-card" />}
                </React.Fragment>
              ))}
              <div ref={sentinelRef} styleName="scroll-sentinel" />
              {loadingNextPage && (
                <div styleName="loading-row">
                  <Spinner show size="sm" />
                  <span>Loading more coupons…</span>
                </div>
              )}
            </div>
          )}

          <p styleName="footer-note">
            To search through all coupons{' '}
            <button type="button" styleName="footer-link" onClick={() => navigate(RouteNames.COUPONS_LISTS_ALL)}>
              click here
            </button>
          </p>
        </div>
      </div>

      <LoginPromptModal isOpen={isLoginPromptOpen} onClose={() => setIsLoginPromptOpen(false)} />
    </div>
  );
};

const LoginPromptModal = LoginPromptModalBase;

export default CSSModule(GuestHome, styles, { allowMultiple: true });